import React, { useState } from "react";
import {
  Box,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseButton from "./CloseButton";
import CustomIcon from "./CustomIcon";
import {
  ChangedChip,
  OptionItem,
  SectionLabel,
  StyledDialog,
} from "./EditVehicleDetails/EditDialogShared";
import ChangeChassisNumberView from "./EditVehicleDetails/ChangeChassisNumberView";
import ChangeLicensePlateView from "./EditVehicleDetails/ChangeLicensePlateView";
import ChangeOriginLocationView from "./EditVehicleDetails/ChangeOriginLocationView";
import ChangeIotDeviceView from "./EditVehicleDetails/ChangeIotDeviceView";
import ChangeVehicleConfigView from "./EditVehicleDetails/ChangeVehicleConfigView";
import { useToast } from "../Hooks/useToast";
import { useBatteryInfo, useVehicleInfo } from "../Pages/DigitalTwinV3/DigitalTwinContext";
import { getVehicleInfo } from "../Api/VehicleApi";
import { getRealTimeInfo } from "../Api/DigitalTwin";
import { usePermissions } from "../Context/PermissionContext";
import { FEATURE_CODES } from "../Static/FeatureMapping";
import ChangeCustomerAccountView from "./EditVehicleDetails/ChangeCustomerAccountView";
import { useOrgLabels } from "../Hooks/usePagesContent";
import { useCanCriticalUpdateVehicle } from "../Helper/Permissions/CriticalUpdate";

// The critical configuration view lays current values and the form side by side,
// so it needs more than the 560px the single-field views use. Same pattern as
// ReviewWorkOrderDialog's WIDE_PAPER.
const WIDE_DIALOG_PAPER = { "& .MuiDialog-paper": { width: "960px", maxWidth: "94vw" } };

const VIEWS = {
  MENU:             "menu",
  CHASSIS_NUMBER:   "changeChassisNumber",
  LICENSE_PLATE:    "changeLicensePlate",
  ORIGIN_LOCATION:  "changeOriginLocation",
  IOT_DEVICE:       "changeIotDevice",
  CUSTOMER_ACCOUNT: "changeCustomerAccount",
  VEHICLE_CONFIG:   "changeVehicleConfig",
};

const EditVehicleDetailsDialog = () => {
  const { openNotification } = useToast();
  const { hasPermission } = usePermissions();
  const { batteryID, setBatteryInfo } = useBatteryInfo();
  const { setVehicleInfo } = useVehicleInfo();
  const { fleetLabel } = useOrgLabels();
  // RBAC v2 + explicit DIGITAL_TWIN.critical_update only; see the helper.
  const canCriticalUpdate = useCanCriticalUpdateVehicle();

  const [isOpen, setIsOpen]         = useState(false);
  const [currentView, setCurrentView] = useState(VIEWS.MENU);

  const handleOpen  = () => setIsOpen(true);

  const handleClose = () => {
    setCurrentView(VIEWS.MENU);
    setIsOpen(false);
  };

  const handleSuccess = async (toastText) => {
    try {
      const [updatedVehicle, updatedRealTime] = await Promise.all([
        getVehicleInfo(batteryID),
        getRealTimeInfo(batteryID),
      ]);
      if (updatedVehicle?.response?.data) {
        setVehicleInfo(updatedVehicle.response.data);
      }
      if (updatedRealTime?.data) {
        setBatteryInfo(updatedRealTime.data);
      }
    } catch {
      // Refresh failure should not block the success flow —
      // the mutation already succeeded. Context will be stale
      // until the user navigates away and back.
    }
    openNotification({ text: toastText, icon: "toast-success" });
    handleClose();
  };

  if (!hasPermission(FEATURE_CODES.DIGITAL_TWIN, "update")) return null;

  const backToMenu = () => setCurrentView(VIEWS.MENU);

  return (
    <>
      <IconButton
        data-testid="edit-vehicle-details-btn"
        sx={{ p: 0, "&:hover svg": { color: "#6D72F6" } }}
        onClick={handleOpen}
      >
        <CustomIcon name="edit-icon" />
      </IconButton>

      <StyledDialog
        open={isOpen}
        onClose={() => {}}
        sx={currentView === VIEWS.VEHICLE_CONFIG ? WIDE_DIALOG_PAPER : undefined}
      >
        <DialogContent sx={{ p: 0, minHeight: "200px" }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomIcon name="edit-icon" />
              <Typography
                variant="fw600s16c000000"
                sx={{ fontSize: "20px", lineHeight: "20px", color: "#101111" }}
              >
                Edit Vehicle Details
              </Typography>
            </Box>
            <CloseButton onClick={handleClose} />
          </Box>

          <Divider sx={{ my: 3, borderColor: "#E6E6E6" }} />

          {/* Menu */}
          {currentView === VIEWS.MENU && (
            <Grid2 container flexDirection="column" gap={1.5}>
              <Typography variant="fw500s16c000000">
                Choose what you would like to edit
              </Typography>

              {[
                { label: "Change Chassis Number",        view: VIEWS.CHASSIS_NUMBER },
                { label: "Change License Plate Number",  view: VIEWS.LICENSE_PLATE },
                { label: "Change Origin Location",       view: VIEWS.ORIGIN_LOCATION },
                { label: "Change IOT Device",            view: VIEWS.IOT_DEVICE },
                { label: `Change ${fleetLabel}`,         view: VIEWS.CUSTOMER_ACCOUNT },
              ].map(({ label, view }) => (
                <OptionItem key={view} onClick={() => setCurrentView(view)}>
                  <Typography
                    variant="fw400s14c000000"
                    sx={{ color: "inherit !important" }}
                  >
                    {label}
                  </Typography>
                  <Box component="span">
                    <KeyboardArrowRightIcon sx={{ fontSize: "20px" }} />
                  </Box>
                </OptionItem>
              ))}

              {canCriticalUpdate && (
                <>
                  {/* The column's gap already spaces this from the option below
                      it, so the label's own bottom margin is dropped here. */}
                  <Box sx={{ mt: 1, mb: -1.5 }}>
                    <SectionLabel>Critical Configuration</SectionLabel>
                  </Box>
                  <OptionItem
                    data-testid="edit-vehicle-critical-config-option"
                    onClick={() => setCurrentView(VIEWS.VEHICLE_CONFIG)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="fw400s14c000000"
                        sx={{ color: "inherit !important" }}
                      >
                        Change Project, Protocol &amp; Bus Type
                      </Typography>
                      <ChangedChip label="Critical" tone="critical" />
                    </Box>
                    <Box component="span">
                      <KeyboardArrowRightIcon sx={{ fontSize: "20px" }} />
                    </Box>
                  </OptionItem>
                </>
              )}
            </Grid2>
          )}

          {/* Sub-views */}
          {currentView === VIEWS.CHASSIS_NUMBER && (
            <ChangeChassisNumberView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
          {currentView === VIEWS.LICENSE_PLATE && (
            <ChangeLicensePlateView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
          {currentView === VIEWS.ORIGIN_LOCATION && (
            <ChangeOriginLocationView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
          {currentView === VIEWS.IOT_DEVICE && (
            <ChangeIotDeviceView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
          {currentView === VIEWS.CUSTOMER_ACCOUNT && (
            <ChangeCustomerAccountView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
          {currentView === VIEWS.VEHICLE_CONFIG && canCriticalUpdate && (
            <ChangeVehicleConfigView onBack={backToMenu} onSuccess={handleSuccess} />
          )}
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default EditVehicleDetailsDialog;
